import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { AppHeader } from "@/components/app-header";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Users,
  Video,
  Upload,
  UserCheck,
  Shield,
  Save,
  Loader2,
  MessageSquare,
  Mail,
  Send,
  CheckCircle,
  AlertCircle,
  Plus,
  Pencil,
  Trash2,
  FileText,
  ChevronDown,
  X,
  Link as LinkIcon,
  Image,
} from "lucide-react";
import { useState, useEffect } from "react";

interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  totalVideos: number;
  uploadedVideos: number;
  tierBreakdown: { tier: string; value: number }[];
}

interface AdminUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  profileImageUrl: string | null;
  subscriptionTier: string;
  isAdmin: string;
  adminNotes: string | null;
  createdAt: string | null;
  videoCount: number;
}

interface BlastBodyData {
  bodyText: string;
  images: string[];
  links: { url: string; text: string }[];
  ctaText: string;
  ctaUrl: string;
}

function emptyBodyData(): BlastBodyData {
  return { bodyText: "", images: [], links: [], ctaText: "", ctaUrl: "" };
}

function parseBodyData(raw: string): BlastBodyData {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && "bodyText" in parsed) {
      return {
        bodyText: parsed.bodyText || "",
        images: Array.isArray(parsed.images) ? parsed.images : [],
        links: Array.isArray(parsed.links) ? parsed.links : [],
        ctaText: parsed.ctaText || "",
        ctaUrl: parsed.ctaUrl || "",
      };
    }
  } catch {}
  return { bodyText: raw, images: [], links: [], ctaText: "", ctaUrl: "" };
}

interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

interface TemplateFormState {
  name: string;
  subject: string;
  bodyData: BlastBodyData;
}

function StatCard({
  label,
  value,
  icon: Icon,
  testId,
}: {
  label: string;
  value: number | string;
  icon: any;
  testId: string;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-md bg-primary/10">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold" data-testid={testId}>
            {value}
          </p>
        </div>
      </div>
    </Card>
  );
}

function UserRow({
  user,
  onUpdate,
  isPending,
}: {
  user: AdminUser;
  onUpdate: (id: string, data: { subscriptionTier?: string; adminNotes?: string }) => void;
  isPending: boolean;
}) {
  const [notes, setNotes] = useState(user.adminNotes || "");
  const [notesChanged, setNotesChanged] = useState(false);

  const handleNotesChange = (val: string) => {
    setNotes(val);
    setNotesChanged(val !== (user.adminNotes || ""));
  };

  const handleSaveNotes = () => {
    onUpdate(user.id, { adminNotes: notes });
    setNotesChanged(false);
  };

  const tierColors: Record<string, string> = {
    free: "",
    monthly: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
    yearly: "bg-purple-500/15 text-purple-700 dark:text-purple-300",
  };

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border-b last:border-b-0"
      data-testid={`row-user-${user.id}`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Avatar className="h-9 w-9 shrink-0">
          <AvatarImage src={user.profileImageUrl || undefined} alt={user.firstName || "User"} />
          <AvatarFallback className="text-xs">
            {(user.firstName?.[0] || "")}{(user.lastName?.[0] || "")}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium truncate" data-testid={`text-name-${user.id}`}>
              {user.firstName || ""} {user.lastName || ""}
            </p>
            {user.isAdmin === "true" && (
              <Badge variant="outline" className="text-xs no-default-hover-elevate no-default-active-elevate gap-1">
                <Shield className="w-3 h-3" /> Admin
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground truncate" data-testid={`text-email-${user.id}`}>
            {user.email || "No email"}
          </p>
          <p className="text-xs text-muted-foreground">
            {user.videoCount} saved video{user.videoCount !== 1 ? "s" : ""}
            {user.createdAt && (
              <span> &middot; Joined {new Date(user.createdAt).toLocaleDateString()}</span>
            )}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
        <Select
          value={user.subscriptionTier}
          onValueChange={(val) => onUpdate(user.id, { subscriptionTier: val })}
          disabled={isPending}
        >
          <SelectTrigger
            className={`w-[120px] ${tierColors[user.subscriptionTier] || ""}`}
            data-testid={`select-tier-${user.id}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="yearly">Yearly</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1 flex-1 min-w-[140px]">
          <Input
            placeholder="Add a note..."
            value={notes}
            onChange={(e) => handleNotesChange(e.target.value)}
            className="text-sm"
            data-testid={`input-notes-${user.id}`}
          />
          {notesChanged && (
            <Button
              size="icon"
              variant="ghost"
              onClick={handleSaveNotes}
              disabled={isPending}
              data-testid={`button-save-notes-${user.id}`}
            >
              {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function BodyDataForm({
  bodyData,
  onChange,
}: {
  bodyData: BlastBodyData;
  onChange: (d: BlastBodyData) => void;
}) {
  const update = (updates: Partial<BlastBodyData>) => onChange({ ...bodyData, ...updates });

  const addImage = () => update({ images: [...bodyData.images, ""] });
  const removeImage = (i: number) => update({ images: bodyData.images.filter((_, idx) => idx !== i) });
  const setImage = (i: number, val: string) => {
    const images = [...bodyData.images];
    images[i] = val;
    update({ images });
  };

  const addLink = () => update({ links: [...bodyData.links, { text: "", url: "" }] });
  const removeLink = (i: number) => update({ links: bodyData.links.filter((_, idx) => idx !== i) });
  const setLink = (i: number, key: "text" | "url", val: string) => {
    const links = [...bodyData.links];
    links[i] = { ...links[i], [key]: val };
    update({ links });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
          Body text <span className="font-normal">(use [First Name] to personalize)</span>
        </label>
        <Textarea
          placeholder="Write your email body here..."
          value={bodyData.bodyText}
          onChange={(e) => update({ bodyText: e.target.value })}
          className="text-sm min-h-[160px] resize-y"
          data-testid="textarea-body-text"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <Image className="w-3.5 h-3.5" /> Images
          </label>
          <Button
            variant="ghost"
            size="sm"
            onClick={addImage}
            className="h-6 text-xs gap-1 px-2"
            data-testid="button-add-image"
          >
            <Plus className="w-3 h-3" /> Add image
          </Button>
        </div>
        {bodyData.images.map((url, i) => (
          <div key={i} className="flex gap-1.5 mb-1.5">
            <Input
              placeholder="Image URL (https://...)"
              value={url}
              onChange={(e) => setImage(i, e.target.value)}
              className="text-sm"
              data-testid={`input-image-${i}`}
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={() => removeImage(i)}
              data-testid={`button-remove-image-${i}`}
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
        {bodyData.images.length === 0 && (
          <p className="text-xs text-muted-foreground">No images — click "Add image" to include one.</p>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <LinkIcon className="w-3.5 h-3.5" /> Links
          </label>
          <Button
            variant="ghost"
            size="sm"
            onClick={addLink}
            className="h-6 text-xs gap-1 px-2"
            data-testid="button-add-link"
          >
            <Plus className="w-3 h-3" /> Add link
          </Button>
        </div>
        {bodyData.links.map((link, i) => (
          <div key={i} className="flex gap-1.5 mb-1.5">
            <Input
              placeholder="Label"
              value={link.text}
              onChange={(e) => setLink(i, "text", e.target.value)}
              className="text-sm w-[120px] shrink-0"
              data-testid={`input-link-text-${i}`}
            />
            <Input
              placeholder="URL (https://...)"
              value={link.url}
              onChange={(e) => setLink(i, "url", e.target.value)}
              className="text-sm flex-1"
              data-testid={`input-link-url-${i}`}
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={() => removeLink(i)}
              data-testid={`button-remove-link-${i}`}
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
        {bodyData.links.length === 0 && (
          <p className="text-xs text-muted-foreground">No links — click "Add link" to include one.</p>
        )}
      </div>

      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
          CTA button <span className="font-normal">(optional)</span>
        </label>
        <div className="flex gap-1.5">
          <Input
            placeholder="Button text"
            value={bodyData.ctaText}
            onChange={(e) => update({ ctaText: e.target.value })}
            className="text-sm w-[140px] shrink-0"
            data-testid="input-cta-text"
          />
          <Input
            placeholder="Button URL (https://...)"
            value={bodyData.ctaUrl}
            onChange={(e) => update({ ctaUrl: e.target.value })}
            className="text-sm flex-1"
            data-testid="input-cta-url"
          />
        </div>
      </div>
    </div>
  );
}

function EmailTemplatesSection({
  onLoadTemplate,
}: {
  onLoadTemplate: (t: EmailTemplate) => void;
}) {
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<number | "new" | null>(null);
  const [form, setForm] = useState<TemplateFormState>({ name: "", subject: "", bodyData: emptyBodyData() });
  const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null);

  const templatesQuery = useQuery<EmailTemplate[]>({
    queryKey: ["/api/admin/email-templates"],
  });

  const createMutation = useMutation({
    mutationFn: async (data: TemplateFormState) => {
      const res = await apiRequest("POST", "/api/admin/email-templates", {
        name: data.name,
        subject: data.subject,
        body: JSON.stringify(data.bodyData),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-templates"] });
      setEditingId(null);
      toast({ title: "Template saved" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err?.message || "Failed to save template.", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<TemplateFormState> }) => {
      const res = await apiRequest("PATCH", `/api/admin/email-templates/${id}`, {
        name: data.name,
        subject: data.subject,
        body: data.bodyData ? JSON.stringify(data.bodyData) : undefined,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-templates"] });
      setEditingId(null);
      toast({ title: "Template updated" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err?.message || "Failed to update template.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("DELETE", `/api/admin/email-templates/${id}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-templates"] });
      setDeleteTarget(null);
      toast({ title: "Template deleted" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete template.", variant: "destructive" });
    },
  });

  const startNew = () => {
    setForm({ name: "", subject: "", bodyData: emptyBodyData() });
    setEditingId("new");
  };

  const startEdit = (t: EmailTemplate) => {
    setForm({ name: t.name, subject: t.subject, bodyData: parseBodyData(t.body) });
    setEditingId(t.id);
  };

  const cancelEdit = () => setEditingId(null);

  const handleSave = () => {
    if (!form.name.trim() || !form.subject.trim() || !form.bodyData.bodyText.trim()) {
      toast({ title: "Name, subject, and body text are required", variant: "destructive" });
      return;
    }
    if (editingId === "new") {
      createMutation.mutate(form);
    } else if (typeof editingId === "number") {
      updateMutation.mutate({ id: editingId, data: form });
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const templates = templatesQuery.data || [];

  return (
    <Card className="mt-6">
      <div className="p-4 border-b flex items-center justify-between gap-3">
        <h3 className="font-semibold flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Email Templates
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={startNew}
          disabled={editingId !== null}
          className="gap-1.5"
          data-testid="button-new-template"
        >
          <Plus className="w-3.5 h-3.5" />
          New Template
        </Button>
      </div>

      {templatesQuery.isLoading ? (
        <div className="p-8 text-center">
          <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
        </div>
      ) : (
        <div>
          {editingId === "new" && (
            <TemplateForm
              form={form}
              onChange={setForm}
              onSave={handleSave}
              onCancel={cancelEdit}
              isSaving={isSaving}
              isNew
            />
          )}

          {templates.length === 0 && editingId !== "new" && (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No templates yet. Create one to get started.
            </div>
          )}

          {templates.map((t) => (
            <div key={t.id}>
              {editingId === t.id ? (
                <TemplateForm
                  form={form}
                  onChange={setForm}
                  onSave={handleSave}
                  onCancel={cancelEdit}
                  isSaving={isSaving}
                />
              ) : (
                <div
                  className="flex items-start gap-3 p-4 border-b last:border-b-0 group"
                  data-testid={`row-template-${t.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate" data-testid={`text-template-name-${t.id}`}>{t.name}</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{t.subject}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onLoadTemplate(t)}
                      className="gap-1.5 text-xs h-7"
                      data-testid={`button-load-template-${t.id}`}
                    >
                      <ChevronDown className="w-3 h-3" />
                      Load
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => startEdit(t)}
                      disabled={editingId !== null}
                      data-testid={`button-edit-template-${t.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(t)}
                      data-testid={`button-delete-template-${t.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="dialog-delete-template-confirm">
          <DialogHeader>
            <DialogTitle>Delete template?</DialogTitle>
            <DialogDescription>
              "{deleteTarget?.name}" will be permanently deleted. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="button-delete-template-cancel">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              disabled={deleteMutation.isPending}
              data-testid="button-delete-template-confirm"
            >
              {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function TemplateForm({
  form,
  onChange,
  onSave,
  onCancel,
  isSaving,
  isNew = false,
}: {
  form: TemplateFormState;
  onChange: (f: TemplateFormState) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
  isNew?: boolean;
}) {
  return (
    <div className="p-4 border-b bg-muted/30 space-y-3" data-testid={isNew ? "form-new-template" : "form-edit-template"}>
      <Input
        placeholder="Template name (e.g. Early Access Offer)"
        value={form.name}
        onChange={(e) => onChange({ ...form, name: e.target.value })}
        data-testid="input-template-name"
      />
      <Input
        placeholder="Subject line"
        value={form.subject}
        onChange={(e) => onChange({ ...form, subject: e.target.value })}
        data-testid="input-template-subject"
      />
      <BodyDataForm
        bodyData={form.bodyData}
        onChange={(bodyData) => onChange({ ...form, bodyData })}
      />
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSaving} data-testid="button-template-cancel">
          <X className="w-3.5 h-3.5 mr-1" /> Cancel
        </Button>
        <Button size="sm" onClick={onSave} disabled={isSaving} data-testid="button-template-save">
          {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          {isNew ? "Create template" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

export default function AdminPage() {
  useEffect(() => { document.title = "Admin | DanceMirror"; }, []);
  const auth = useAuth();
  const { toast } = useToast();
  const [blastSubject, setBlastSubject] = useState("");
  const [blastBodyData, setBlastBodyData] = useState<BlastBodyData>(emptyBodyData());

  const statsQuery = useQuery<AdminStats>({
    queryKey: ["/api/admin/stats"],
    enabled: auth.isAuthenticated,
  });

  const usersQuery = useQuery<AdminUser[]>({
    queryKey: ["/api/admin/users"],
    enabled: auth.isAuthenticated,
  });

  const updateUserMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, any> }) => {
      const res = await apiRequest("PATCH", `/api/admin/users/${id}`, data);
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      const action = variables.data.subscriptionTier ? "Tier updated" : "Notes saved";
      toast({ title: action, description: `User updated successfully.` });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update user.", variant: "destructive" });
    },
  });

  const handleUpdateUser = (id: string, data: { subscriptionTier?: string; adminNotes?: string }) => {
    updateUserMutation.mutate({ id, data });
  };

  const discordReportMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/discord/report");
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Report sent", description: "Utilization report posted to Discord." });
    },
    onError: (err: any) => {
      const msg = err?.message || "Failed to send Discord report.";
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  if (auth.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="max-w-5xl mx-auto px-4 py-20 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
        </main>
      </div>
    );
  }

  if (!auth.isAuthenticated || !auth.user) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="max-w-5xl mx-auto px-4 py-20 text-center">
          <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Admin Access Required</h2>
          <p className="text-muted-foreground">Please log in with an admin account.</p>
        </main>
      </div>
    );
  }

  const isAdminUser = auth.user.isAdmin === "true";

  if (!isAdminUser) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="max-w-5xl mx-auto px-4 py-20 text-center">
          <Shield className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You don't have admin privileges.</p>
        </main>
      </div>
    );
  }

  const stats = statsQuery.data;
  const allUsers = usersQuery.data || [];

  const handleLoadTemplate = (t: EmailTemplate) => {
    if (blastSubject.trim() || blastBodyData.bodyText.trim()) {
      if (!confirm(`Load "${t.name}"? This will replace your current draft.`)) return;
    }
    setBlastSubject(t.subject);
    setBlastBodyData(parseBodyData(t.body));
    toast({ title: `Loaded "${t.name}"` });
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      <main className="max-w-5xl mx-auto px-4 py-8 sm:py-12">
        <div className="flex items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold" data-testid="text-admin-title">Admin Dashboard</h2>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => discordReportMutation.mutate()}
            disabled={discordReportMutation.isPending}
            data-testid="button-discord-report"
          >
            {discordReportMutation.isPending
              ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
              : <MessageSquare className="w-4 h-4 mr-2" />}
            Send Discord Report
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <StatCard label="Total Users" value={stats?.totalUsers ?? "-"} icon={Users} testId="stat-total-users" />
          <StatCard label="New (7d)" value={stats?.activeUsers ?? "-"} icon={UserCheck} testId="stat-active-users" />
          <StatCard label="Saved Videos" value={stats?.totalVideos ?? "-"} icon={Video} testId="stat-total-videos" />
          <StatCard label="Uploads" value={stats?.uploadedVideos ?? "-"} icon={Upload} testId="stat-uploaded-videos" />
        </div>

        {stats?.tierBreakdown && stats.tierBreakdown.length > 0 && (
          <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="tier-breakdown">
            <span className="text-sm text-muted-foreground mr-1">Tiers:</span>
            {stats.tierBreakdown.map((t) => (
              <Badge key={t.tier} variant="outline" className="no-default-hover-elevate no-default-active-elevate">
                {t.tier}: {t.value}
              </Badge>
            ))}
          </div>
        )}

        <Card>
          <div className="p-4 border-b">
            <h3 className="font-semibold flex items-center gap-2">
              <Users className="w-4 h-4" />
              All Users ({allUsers.length})
            </h3>
          </div>

          {usersQuery.isLoading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : allUsers.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No users found.
            </div>
          ) : (
            <div data-testid="user-list">
              {allUsers.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  onUpdate={handleUpdateUser}
                  isPending={updateUserMutation.isPending}
                />
              ))}
            </div>
          )}
        </Card>

        <EmailTemplatesSection onLoadTemplate={handleLoadTemplate} />
        <EmailBlastSectionControlled
          userCount={allUsers.length}
          subject={blastSubject}
          bodyData={blastBodyData}
          onSubjectChange={setBlastSubject}
          onBodyDataChange={setBlastBodyData}
          onLoadTemplate={handleLoadTemplate}
        />
      </main>
    </div>
  );
}

function EmailBlastSectionControlled({
  userCount,
  subject,
  bodyData,
  onSubjectChange,
  onBodyDataChange,
  onLoadTemplate,
}: {
  userCount: number;
  subject: string;
  bodyData: BlastBodyData;
  onSubjectChange: (v: string) => void;
  onBodyDataChange: (v: BlastBodyData) => void;
  onLoadTemplate: (t: EmailTemplate) => void;
}) {
  const { toast } = useToast();
  const templatesQuery = useQuery<EmailTemplate[]>({
    queryKey: ["/api/admin/email-templates"],
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [blastResult, setBlastResult] = useState<{ sent: number; failed: number } | null>(null);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");

  const saveAsTemplateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/email-templates", {
        name: newTemplateName.trim(),
        subject,
        body: JSON.stringify(bodyData),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-templates"] });
      setSaveTemplateOpen(false);
      setNewTemplateName("");
      toast({ title: "Template saved" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err?.message || "Failed to save template.", variant: "destructive" });
    },
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/email-blast/test", { subject, bodyData });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Test email sent", description: "Check your inbox — subject starts with [TEST]." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to send test email.", variant: "destructive" });
    },
  });

  const blastMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/email-blast", { subject, bodyData });
      return res.json();
    },
    onSuccess: (data) => {
      setBlastResult(data);
      setConfirmOpen(false);
      toast({ title: "Blast sent", description: `Sent to ${data.sent} users.` });
    },
    onError: () => {
      setConfirmOpen(false);
      toast({ title: "Error", description: "Failed to send email blast.", variant: "destructive" });
    },
  });

  const hasContent = subject.trim() && bodyData.bodyText.trim();

  return (
    <Card className="mt-6">
      <div className="p-4 border-b flex items-center justify-between gap-3">
        <h3 className="font-semibold flex items-center gap-2">
          <Mail className="w-4 h-4" />
          Email Blast
        </h3>
        <span className="text-sm text-muted-foreground">
          Will send to <strong>{userCount}</strong> user{userCount !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="p-4 space-y-4">
        {templatesQuery.data && templatesQuery.data.length > 0 && (
          <div>
            <label className="text-sm font-medium mb-1.5 block text-muted-foreground">Load template</label>
            <Select
              onValueChange={(id) => {
                const t = templatesQuery.data?.find((t) => String(t.id) === id);
                if (t) onLoadTemplate(t);
              }}
              value=""
            >
              <SelectTrigger data-testid="select-load-template">
                <SelectValue placeholder="Choose a template to load…" />
              </SelectTrigger>
              <SelectContent>
                {templatesQuery.data.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)} data-testid={`option-template-${t.id}`}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div>
          <label className="text-sm font-medium mb-1.5 block text-muted-foreground">Subject</label>
          <Input
            value={subject}
            onChange={(e) => onSubjectChange(e.target.value)}
            placeholder="Email subject..."
            data-testid="input-blast-subject"
          />
        </div>

        <BodyDataForm bodyData={bodyData} onChange={onBodyDataChange} />

        {blastResult && (
          <div
            className={`flex items-center gap-2 p-3 rounded-md text-sm ${
              blastResult.failed === 0
                ? "bg-green-500/10 text-green-600 dark:text-green-400"
                : "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400"
            }`}
            data-testid="text-blast-result"
          >
            {blastResult.failed === 0 ? (
              <CheckCircle className="w-4 h-4 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 shrink-0" />
            )}
            Sent to {blastResult.sent} user{blastResult.sent !== 1 ? "s" : ""}
            {blastResult.failed > 0 && ` · ${blastResult.failed} failed`}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setNewTemplateName("");
              setSaveTemplateOpen(true);
            }}
            disabled={!hasContent}
            className="gap-1.5 text-xs"
            data-testid="button-save-as-template"
          >
            <Save className="w-3.5 h-3.5" />
            Save as template
          </Button>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => testMutation.mutate()}
              disabled={!hasContent || testMutation.isPending || blastMutation.isPending}
              className="gap-2"
              data-testid="button-send-test"
            >
              {testMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Mail className="w-4 h-4" />
              )}
              {testMutation.isPending ? "Sending..." : "Send test to me"}
            </Button>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={!hasContent || blastMutation.isPending || testMutation.isPending}
              className="gap-2"
              data-testid="button-send-blast"
            >
              {blastMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {blastMutation.isPending ? "Sending..." : "Send to all users"}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="dialog-blast-confirm">
          <DialogHeader>
            <DialogTitle>Send email blast?</DialogTitle>
            <DialogDescription>
              This will send <strong>"{subject}"</strong> to all{" "}
              <strong>{userCount} registered users</strong>. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              data-testid="button-blast-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => blastMutation.mutate()}
              disabled={blastMutation.isPending}
              data-testid="button-blast-confirm"
            >
              {blastMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Yes, send now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
        <DialogContent data-testid="dialog-save-as-template">
          <DialogHeader>
            <DialogTitle>Save as template</DialogTitle>
            <DialogDescription>
              Give this template a name so you can reuse it later.
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Template name (e.g. Early Access Offer)"
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && newTemplateName.trim() && saveAsTemplateMutation.mutate()}
            data-testid="input-save-template-name"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSaveTemplateOpen(false)}
              data-testid="button-save-template-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => saveAsTemplateMutation.mutate()}
              disabled={!newTemplateName.trim() || saveAsTemplateMutation.isPending}
              data-testid="button-save-template-confirm"
            >
              {saveAsTemplateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
